import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  ENTERPRISE_INTAKE_CONFIDENTIALITY_NOTE,
  ENTERPRISE_INTAKE_SECTION_01_CONTACT,
  ENTERPRISE_INTAKE_SECTION_02_MODULE_SELECTOR,
  ENTERPRISE_INTAKE_SECTION_03_USERS,
  ENTERPRISE_INTAKE_SECTION_04_SUPPORT_CONTRACT,
  ENTERPRISE_INTAKE_SECTION_05_ADDITIONAL_NOTES,
  ENTERPRISE_INTAKE_SECTION_MODULE_1_CREATIVE_ASSETS,
  ENTERPRISE_INTAKE_SECTION_MODULE_2_PAID_MEDIA_LAUNCH,
  ENTERPRISE_INTAKE_SECTION_MODULE_3_PAID_MEDIA_IMPORT,
  ENTERPRISE_INTAKE_SECTION_MODULE_4_ORGANIC_SOCIAL,
  ENTERPRISE_INTAKE_SUBMIT_BUTTON,
  ENTERPRISE_INTAKE_SUBMIT_SUCCESS_MESSAGE,
  ENTERPRISE_INTAKE_SUBMIT_SUCCESS_TITLE,
  ERROR_OCCURRED,
  OK,
} from "../../i18n/keysTranslations";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import { saveEnterpriseIntakeFormRequest } from "../../actions/enterpriseIntake";
import Navbar from "../PreActivation/components/Navbar";
import Card from "../PreActivation/components/Card";
import { PAPER_BACKGROUND_COLOR_SLIDE_MENU } from "../../utils/colors";
import { APP_SUPPORTED_LANGUAGES } from "../../utils/constants";
import { createEnterpriseIntakeSchema } from "./enterpriseIntakeSchema";
import Button from "../../components/Buttons/Button";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import LanguageSwitcher from "../../components/Common/LanguageSwitcher";
import i18next from "i18next";
import HeroSection from "./sections/HeroSection";
import ContactSection from "./sections/ContactSection";
import ModuleSelectorSection from "./sections/ModuleSelectorSection";
import Module1CreativeAssetsSection from "./sections/modules/Module1CreativeAssetsSection";
import Module2PaidMediaLaunchSection from "./sections/modules/Module2PaidMediaLaunchSection";
import Module3PaidMediaImportSection from "./sections/modules/Module3PaidMediaImportSection";
import Module4OrganicSocialSection from "./sections/modules/Module4OrganicSocialSection";
import UsersSection from "./sections/UsersSection";
import SupportContractSection from "./sections/SupportContractSection";
import AdditionalNotesSection from "./sections/AdditionalNotesSection";
import {
  enterpriseIntakeDefaultValues,
  getEnterpriseIntakeModuleVisibility,
} from "./useEnterpriseIntakeModuleVisibility";

const getNormalizedLanguage = (language = "") => {
  if (!language) return null;
  const shortLanguage = language.toLowerCase().split("-")[0];
  return APP_SUPPORTED_LANGUAGES.includes(shortLanguage) ? shortLanguage : null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const getBrowserLanguage = () => {
  const browserLanguage =
    navigator.language || navigator.languages?.[0] || "en";
  return getNormalizedLanguage(browserLanguage) || "en";
};

const LANGUAGE_SWITCHER_BUTTON_STYLES = {
  minWidth: "max-content",
  border: "none !important",
  boxShadow: "none !important",
  backgroundColor: "transparent !important",
  px: "0 !important",
  "&.MuiButtonBase-root.MuiButton-root:hover": {
    backgroundColor: "transparent !important",
    border: "none !important",
    boxShadow: "none !important",
    color: "inherit !important",
  },
};

const LANGUAGE_SWITCHER_MENU_STYLES = {
  "& .MuiPaper-root": {
    marginTop: "8px",
  },
};

const defaultFormValues = enterpriseIntakeDefaultValues;

const EnterpriseIntakeSectionCards = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();
  const selectedAdleModules =
    useWatch({ control, name: "selectedAdleModules" }) ?? [];
  const formValues = useWatch({ control });
  const { showModule1, showModule2, showModule3, showModule4 } =
    getEnterpriseIntakeModuleVisibility({
      selectedAdleModules,
      formValues,
      readOnly,
    });

  return (
    <>
      <Card section={t(ENTERPRISE_INTAKE_SECTION_01_CONTACT)}>
        <ContactSection control={control} errors={errors} readOnly={readOnly} />
      </Card>

      <Card section={t(ENTERPRISE_INTAKE_SECTION_02_MODULE_SELECTOR)}>
        <ModuleSelectorSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      {showModule1 ? (
        <Card section={t(ENTERPRISE_INTAKE_SECTION_MODULE_1_CREATIVE_ASSETS)}>
          <Module1CreativeAssetsSection
            control={control}
            errors={errors}
            readOnly={readOnly}
          />
        </Card>
      ) : null}

      {showModule2 ? (
        <Card section={t(ENTERPRISE_INTAKE_SECTION_MODULE_2_PAID_MEDIA_LAUNCH)}>
          <Module2PaidMediaLaunchSection
            control={control}
            errors={errors}
            readOnly={readOnly}
          />
        </Card>
      ) : null}

      {showModule3 ? (
        <Card section={t(ENTERPRISE_INTAKE_SECTION_MODULE_3_PAID_MEDIA_IMPORT)}>
          <Module3PaidMediaImportSection
            control={control}
            errors={errors}
            readOnly={readOnly}
          />
        </Card>
      ) : null}

      {showModule4 ? (
        <Card section={t(ENTERPRISE_INTAKE_SECTION_MODULE_4_ORGANIC_SOCIAL)}>
          <Module4OrganicSocialSection
            control={control}
            errors={errors}
            readOnly={readOnly}
          />
        </Card>
      ) : null}

      <Card section={t(ENTERPRISE_INTAKE_SECTION_03_USERS)}>
        <UsersSection control={control} errors={errors} readOnly={readOnly} />
      </Card>

      <Card section={t(ENTERPRISE_INTAKE_SECTION_04_SUPPORT_CONTRACT)}>
        <SupportContractSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      <Card section={t(ENTERPRISE_INTAKE_SECTION_05_ADDITIONAL_NOTES)}>
        <AdditionalNotesSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>
    </>
  );
};

const EnterpriseIntakePageShell = ({ children, readOnly }) => (
  <Box
    sx={{
      minHeight: "100vh",
      background: PAPER_BACKGROUND_COLOR_SLIDE_MENU,
      padding: "24px 16px 48px",
    }}
  >
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: "100%",
        maxWidth: readOnly ? "100%" : "640px",
        margin: "0 auto",
      }}
    >
      {children}
    </Container>
  </Box>
);

const OwnerBackBar = ({ backTo }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
      <IconButton
        aria-label="back"
        onClick={() => navigate(backTo)}
        edge="start"
        size="small"
        sx={{ color: "text.primary" }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Box>
  );
};

const EnterpriseIntakeSubmitForm = ({
  onSubmit,
  submitError,
  isSaving,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ width: "100%" }} noValidate>
      {children}

      {submitError ? (
        <Typography
          role="alert"
          sx={{
            color: "error.main",
            fontSize: "14px",
            textAlign: "center",
            mb: 1,
          }}
        >
          {submitError}
        </Typography>
      ) : null}

      <Button
        type="submit"
        fullWidth
        color="primary"
        disabled={isSaving}
        sx={{
          mt: 1,
          py: 1.5,
          fontWeight: 600,
          borderRadius: "9px !important",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          {isSaving ? (
            <CircularProgress size={22} sx={{ color: "inherit" }} />
          ) : null}
          <Box component="span">{t(ENTERPRISE_INTAKE_SUBMIT_BUTTON)}</Box>
        </Box>
      </Button>

      <Typography
        component="p"
        variant="body2"
        sx={{
          mt: 2,
          color: "text.secondary",
          textAlign: "center",
        }}
      >
        {t(ENTERPRISE_INTAKE_CONFIDENTIALITY_NOTE)}
      </Typography>
    </Box>
  );
};

const useEnterpriseIntakeLanguage = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const routeLanguage = getNormalizedLanguage(lang);
  const currentLanguage = readOnly
    ? getNormalizedLanguage(i18next.language) || "en"
    : routeLanguage;

  useEffect(() => {
    if (readOnly) return;
    if (!currentLanguage) {
      navigate(`/enterprise-intake/${getBrowserLanguage()}`, { replace: true });
      return;
    }
    if (i18next.language !== currentLanguage) {
      i18next.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, navigate, readOnly]);

  const onChangeLanguage = (language) => {
    const nextLanguage = getNormalizedLanguage(language);
    if (!nextLanguage || nextLanguage === currentLanguage) return;
    i18next.changeLanguage(nextLanguage);
    if (!readOnly) {
      navigate(`/enterprise-intake/${nextLanguage}`);
    }
  };

  return {
    currentLanguage,
    onChangeLanguage,
    shouldWaitForValidLanguage: !readOnly && !currentLanguage,
  };
};

const EnterpriseIntake = ({
  readOnly = false,
  prefilledData = null,
  ownerBackTo = null,
}) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const { currentLanguage, onChangeLanguage, shouldWaitForValidLanguage } =
    useEnterpriseIntakeLanguage({ readOnly });

  const validationSchema = useMemo(() => createEnterpriseIntakeSchema(t), [t]);

  const { control, handleSubmit, reset, formState } = useForm({
    resolver: readOnly ? undefined : zodResolver(validationSchema),
    criteriaMode: "all",
    defaultValues: defaultFormValues,
    mode: readOnly ? "onChange" : "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: !readOnly,
  });

  const { errors } = formState;

  useFocusErrorForm(formState);

  useEffect(() => {
    if (prefilledData) {
      reset(prefilledData);
    }
  }, [prefilledData, reset]);

  if (shouldWaitForValidLanguage) return null;

  const onSubmitValid = async (formValues) => {
    setSubmitError(null);
    setIsSaving(true);
    let data;
    try {
      data = await saveEnterpriseIntakeFormRequest(formValues);
    } catch (err) {
      const message =
        err?.response?.data?.message ?? err?.message ?? t(ERROR_OCCURRED);
      setSubmitError(message);
      setIsSaving(false);
      return;
    }
    setIsSaving(false);

    if (data?.ok) {
      reset();
      await SimpleAlert({
        title: t(ENTERPRISE_INTAKE_SUBMIT_SUCCESS_TITLE),
        text: t(ENTERPRISE_INTAKE_SUBMIT_SUCCESS_MESSAGE),
        icon: "success",
        confirmButtonText: t(OK),
      });
      return;
    }
    setSubmitError(data?.message ?? t(ERROR_OCCURRED));
  };

  const onSubmitInvalid = () => {
    setSubmitError(null);
  };

  const sectionCards = (
    <EnterpriseIntakeSectionCards
      control={control}
      errors={errors}
      readOnly={readOnly}
    />
  );

  return (
    <>
      {!readOnly ? (
        <Box
          sx={{
            position: "fixed",
            top: 40,
            right: 16,
            zIndex: 1300,
          }}
        >
          <LanguageSwitcher
            language={currentLanguage}
            onChangeLanguage={onChangeLanguage}
            colorButton="secondary"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={LANGUAGE_SWITCHER_BUTTON_STYLES}
            sxMenu={LANGUAGE_SWITCHER_MENU_STYLES}
          />
        </Box>
      ) : null}

      <EnterpriseIntakePageShell readOnly={readOnly}>
        {ownerBackTo ? <OwnerBackBar backTo={ownerBackTo} /> : null}
        {!readOnly ? <Navbar /> : null}
        {!readOnly ? (
          <Card>
            <HeroSection />
          </Card>
        ) : null}

        {readOnly ? (
          <Box sx={{ width: "100%" }}>{sectionCards}</Box>
        ) : (
          <EnterpriseIntakeSubmitForm
            onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
            submitError={submitError}
            isSaving={isSaving}
          >
            {sectionCards}
          </EnterpriseIntakeSubmitForm>
        )}
      </EnterpriseIntakePageShell>
    </>
  );
};

export default EnterpriseIntake;
